import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import { createCheckoutSession, formatPrice, simulatePayment } from '../booking/bookingApi';
import { paymentConfirmingId } from '../booking/checkoutHandoff';
import { usePaymentReturn } from './usePaymentReturn';
import { fetchOutstanding, fmtSlot, minutesLeft, OUTSTANDING_KEY } from './outstanding';
import {
  DISMISS_KEY as MATCHDAY_DISMISS_KEY,
  MATCHDAY_OUTSTANDING_KEY,
} from '../matchday/MatchdayPrompt';
import './payment-prompt.css';

/** Keyed by user id so one coach's "not now" never silences another account in the same tab. */
const DISMISS_KEY = 'payment-prompt-dismissed';

/**
 * After-login payment nudge. Shows as soon as the app opens (queued behind the matchday check-in
 * when both are due) whenever the signed-in coach has bookings they still owe for, with Pay now
 * going straight to checkout. Dismissible once per session, never blocking, and scoped to
 * bookings this coach can actually pay for (only the owner's checkout session works). The full
 * list lives on the My payments page, which this links to.
 */
export function PaymentPrompt({ matchdayDismissed = false }: { matchdayDismissed?: boolean }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [payingId, setPayingId] = useState<number | null>(null);

  const enabled = !!user && user.status === 'Active';
  const outstanding = useQuery({
    queryKey: OUTSTANDING_KEY,
    queryFn: fetchOutstanding,
    enabled,
  });

  // The matchday check-in is also a modal on the same shell. Queue behind it rather than
  // stacking two backdrops — this one reappears on the next navigation once that is answered
  // or dismissed. Same query key, so this costs no extra request.
  const matchday = useQuery({
    queryKey: MATCHDAY_OUTSTANDING_KEY,
    queryFn: () => api<unknown[]>('/api/matchday/mine/outstanding'),
    enabled,
  });

  // The Pay now button stays spinning past its createCheckoutSession resolving (the browser is
  // still leaving for Stripe) — but if the coach comes back without paying (browser Back / the
  // PWA sheet's Done button) nothing reloads, so un-stick it and reconcile with Stripe in case
  // they in fact paid (see usePaymentReturn).
  usePaymentReturn(payingId, () => setPayingId(null));

  if (!enabled || dismissed) return null;
  if (sessionStorage.getItem(DISMISS_KEY) === String(user.userId)) return null;

  // matchdayDismissed (live state from the shell) matters as well as sessionStorage: writing to
  // sessionStorage re-renders nothing, so without the prop a "Not now" on the matchday prompt
  // left this one invisible until the next navigation instead of appearing straight away.
  const matchdayShowing = (matchday.data?.length ?? 0) > 0
    && !matchdayDismissed
    && sessionStorage.getItem(MATCHDAY_DISMISS_KEY) !== String(user.userId);
  if (matchdayShowing) return null;

  // A booking whose payment is still being reconciled with Stripe (the coach just landed back
  // from checkout) must not be offered for payment again — the outstanding endpoint still lists
  // it until the confirmation commits.
  const confirming = paymentConfirmingId();
  const rows = (outstanding.data ?? []).filter((r) => r.bookingId !== confirming);
  if (rows.length === 0) return null;

  function dismiss() {
    sessionStorage.setItem(DISMISS_KEY, String(user!.userId));
    setDismissed(true);
  }

  async function pay(bookingId: number) {
    setPayingId(bookingId);
    try {
      const session = await createCheckoutSession(bookingId);
      window.location.href = session.url;
    } catch (err) {
      if (err instanceof ApiError && err.code === 'payments-not-configured') {
        try {
          await simulatePayment(bookingId);
          toast('Payment simulated (dev mode) — booking confirmed', 'success');
          dismiss();
          return;
        } catch {
          toast('Online payment is not set up yet — contact the club', 'error');
        }
      } else {
        toast(err instanceof ApiError ? err.message : 'Could not open checkout — try again', 'error');
      }
      setPayingId(null);
    }
  }

  const total = rows.reduce((sum, r) => sum + r.pricePence, 0);

  return (
    <div className="ui-dialog-backdrop" role="presentation" onClick={dismiss}>
      <div
        className="ui-dialog pay-prompt"
        role="dialog"
        aria-modal="true"
        aria-label="Outstanding payments"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="ui-dialog-title">
          {rows.length === 1 ? 'You have a booking to pay for' : `You have ${rows.length} bookings to pay for`}
        </h2>
        <p className="pay-prompt-intro">
          {rows.length === 1
            ? 'This slot is held for you until it is paid:'
            : `These slots are held for you until they are paid — ${formatPrice(total)} in total:`}
        </p>

        <ul className="pay-prompt-list">
          {rows.map((row) => {
            const left = minutesLeft(row.holdExpiresUtc);
            return (
              <li key={row.bookingId} className="pay-prompt-row">
                <div className="pay-prompt-detail">
                  <span className="pay-prompt-team">{row.teamName}</span>
                  <span className="pay-prompt-slot">
                    {row.pitchName} · {fmtSlot(row.startUtc, row.endUtc)}
                  </span>
                  <span className="pay-prompt-venue">{row.venueName}</span>
                  {left !== null ? (
                    <span className={`pay-prompt-hold ${left <= 5 ? 'pay-prompt-hold-urgent' : ''}`}>
                      {left === 0 ? 'Hold about to expire' : `Held for ${left} more minute${left === 1 ? '' : 's'}`}
                    </span>
                  ) : null}
                </div>
                <div className="pay-prompt-action">
                  <span className="pay-prompt-price">{formatPrice(row.pricePence)}</span>
                  <Button
                    size="sm"
                    loading={payingId === row.bookingId}
                    disabled={payingId !== null}
                    onClick={() => void pay(row.bookingId)}
                  >
                    Pay now
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="ui-dialog-actions">
          <Button
            variant="secondary"
            disabled={payingId !== null}
            onClick={() => {
              dismiss();
              navigate('/my-payments');
            }}
          >
            View all payments
          </Button>
          <Button variant="ghost" onClick={dismiss} disabled={payingId !== null}>
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}
